import {
  CONTRACT_STATUS,
  SIGNATORY_STATUS,
  VENDOR_FEE_TYPES_VALUES
} from 'features/ContractDetails/constants'
import { IPartialUserInfo } from 'redux/store/user/types'
import { TListItem } from 'components/Forms/forms.user.d'
import { TCommunityHS } from 'features/HealthSystemDetails/types'
import { TResponsibleUser, TRFPDocument } from 'features/RFP/RFPDetails/types'
import { RebateDataRequest } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'
import { TQueryParams } from 'components/Table/types'
import { IContractHealthSystem } from './Facilities/types'
import { HealthSystem } from '../MyCommunity/HealthSystems/types'
import { RECONCILIATION_STATUSES } from '../ContractItems/types'
import { ResponseWithPagination } from '../../constants'
import { TAutoGeneratedContractData } from './ContractDetailsGeneral/ContractDetailsDocuments/UploadMultipleContractDocumentsPopup/types'
import { DOCUMENT_REQUIRED_ACTION } from 'components/Status/constants'

type CreatorResponse = Omit<IPartialUserInfo, 'health_system'> & {
  health_system?: HealthSystem
}

export type ContractDetailsResponse = Omit<IContractDetails, 'creator'> & {
  creator: CreatorResponse
}

export enum ContractCreationWays {
  MANUAL = 'manual',
  AUTO = 'auto'
}

export type IContractDetails = RebateDataRequest & {
  uuid: string
  number: string
  status: ContractStatusUnion
  contract_category_name: string
  start_date: string | null
  pricing_effective_date: string | null
  finish_date: string | null
  primary_finish_date: string | null
  termination_date: string | null
  admin_fee: string | null
  payment_term_days: number | null
  payment_frequency: string | null
  creation_way: ContractCreationWays
  creator: IPartialUserInfo
  vendor: TListItem
  community: TCommunityHS
  rfp: string
  rfp_name: string
  amendment_document: string
  custom_number: string | null
  community_responsibles: TResponsibleUser[]
  vendor_responsibles: TResponsibleUser[]
  commons_responsibles: TResponsibleUser[]
  contract_document_templates: TRFPDocument[]
  contract_attachments: TRFPDocument[]
  contract_attachments_ids?: string[]
  contract_extension_documents: Array<TRFPDocument & { finish_date: string }>
  timelines: IContractTimeline[]
  prs: string
  prs_name: string
  creation_type: string
  health_systems?: IContractHealthSystem[]
  pending_reconciliation?: TPPendingReconciliationObject
  contract_category_id?: string
  led_by: string
  vendor_fee_type: VendorFeeTypeUnion
  note: TNote | null
  contract_category_organization_name?: string
  contract_items_updated_at?: string
  shipping_method: string | null
}

export type TContractDocument = {
  uuid: string
  file: string
  creator?: IPartialUserInfo
  accepted_by: Array<IPartialUserInfo>
  signatories: Array<TSignatory>
  history?: TRFPDocument[]
  created_at?: string
  is_accepted_automatically?: boolean
  is_signed_automatically?: boolean
  is_digital_signature_required?: boolean
  is_redlining_required?: boolean
  can_delete?: boolean
  required_action?: ValueOfObject<typeof DOCUMENT_REQUIRED_ACTION>
}

export type TSignatory = {
  uuid: string
  status: SignatoryStatusUnion
  email?: string
  signatory: IPartialUserInfo | null
  created_by: string
}

export type TDocumentSignatoryToReassign = {
  document: string
  signatory?: TSignatory
}

export type TGetDocumentsHistoryParams = {
  hsId: string
  hospitalId: string | null
}

export type SignatoryStatusUnion = ValueOfObject<typeof SIGNATORY_STATUS>

export type TPPendingReconciliationObject = {
  uuid: string
  required_items_count: number
  user: string
  status: RECONCILIATION_STATUSES
}

export interface IContractTimeline {
  uuid: string
  status: ContractStatusUnion
  expire_at: string
}

export type TContractUploadTempFile = TAutoGeneratedContractData & {
  uploaded_file: string
}

export interface TContractUploadFile {
  contract: string
  files: TContractUploadTempFile[]
}

export interface TContractAttchmentUploadFile {
  contract: string
  file: FormDataEntryValue | null
}

export interface TContractDocumentTempStore {
  uuid: string
  file: string
  file_name: string
}

export interface TContractAcceptPayload {
  contractId: string
  documentId: string
}

export type ContractStatusUnion = ValueOfObject<typeof CONTRACT_STATUS>

export interface ManualContractCreationRequestData {
  creator: string
  organization_contract_category: string
  name: string
  start_date: string
  primary_finish_date: string
  termination_date: string
  admin_fee: number
  payment_term_days: number | null
  payment_frequency: string | null
  vendor: string
  vendor_contract_steward?: string
  vendor_fee_type: string
  shipping_method: string | null
  custom_number: string | null
}

export type BulkContractCreationRequestData = Omit<
  ManualContractCreationRequestData,
  | 'payment_frequency'
  | 'payment_term_days'
  | 'creator'
  | 'shipping_method'
  | 'custom_number'
> & {
  rebate_type: string | null
  baseline_spend: string | null
  rebate_amount: string | null
  threshold: string | null
}

export type SetupContractDetailsTimeRequestData = Array<
  Omit<IContractTimeline, 'uuid'>
>

export type GetVendorContractStewardsArgs = {
  vendors: string[]
  contract_categories: string[]
}

export type TAvailableOwnersParams = TQueryParams & {
  categories: string | string[]
}

export type TOwnerData = {
  first_name: string
  last_name: string
  uuid: string
  role: string
}

export type TEvaluationDocuments = {
  is_visible_for_community: boolean
  is_visible_for_commons: boolean
}

export type TEvaluationDocumentsUploadResponse = {
  uuid: string
}

export type TNotesResponse = {
  text: string
}

export type TNotesUpdateRequest = {
  text: string
}

export type VendorFeeTypeUnion = ValueOfObject<typeof VENDOR_FEE_TYPES_VALUES>

export type TCSResponse = ResponseWithPagination<{
  uuid: string
  first_name: string
  last_name: string
}>

export type TNote = {
  uuid: string
  text: string
  created_at: string
  created_by: IPartialUserInfo
  updated_at: string | null
  updated_by: IPartialUserInfo | null
}
